import { z } from 'zod';
import { MediaSchema } from './medias.js';
import { SlugSchema, TexteBilingueSchema } from './textes.js';
/** Statut d'œuvre, posé par les événements de la régie (D13). */
export const STATUTS_OEUVRE = ['disponible', 'reservee', 'vendue', 'en-depot', 'archivee'];
export const StatutOeuvreSchema = z.enum(STATUTS_OEUVRE);
export const DevisesSchema = z.enum(['EUR', 'CHF', 'GBP', 'USD']);
/** Prix : entiers en centimes, devise ISO, HT (section 4.2). */
export const PrixSchema = z.object({
    montantCentimes: z.number().int().nonnegative(),
    devise: DevisesSchema,
});
export const DimensionsSchema = z
    .object({
    hauteur: z.number().positive(),
    largeur: z.number().positive(),
    profondeur: z.number().positive().optional(),
    unite: z.literal('cm'),
})
    .or(z.object({
    /** Dimensions variables, ou œuvre pour laquelle seul un texte a du sens. */
    texte: TexteBilingueSchema,
}));
/**
 * Édition (section 4.2) : l'œuvre porte la taille du tirage ; l'exemplaire
 * n'est renseigné que si la galerie suit les exemplaires un par un.
 */
export const EditionSchema = z.object({
    tirage: z.number().int().positive(),
    epreuvesArtiste: z.number().int().nonnegative().optional(),
    exemplaire: z.number().int().positive().optional(),
    /** « Épreuve d'artiste » plutôt qu'un numéro dans le tirage. */
    epreuveArtiste: z.boolean().optional(),
});
export const AchatSchema = z.object({
    montantCentimes: z.number().int().positive().safe(),
    devise: z.literal('EUR'),
    lien: z.string().regex(/^\/api\/boutique\/achat\?oeuvre=[a-z0-9-]+&lang=(fr|en)$/),
});
export const OeuvreSchema = z
    .object({
    slug: SlugSchema,
    /** Les titres d'œuvres ne sont pas traduits (section 4.2). */
    titre: z.string().min(1),
    artiste: SlugSchema,
    annee: z.number().int().min(1400).max(2100).optional(),
    technique: TexteBilingueSchema,
    dimensions: DimensionsSchema.optional(),
    edition: EditionSchema.optional(),
    statut: StatutOeuvreSchema,
    /**
     * Visibilité sur le site. Une œuvre invisible n'apparaît nulle part :
     * ni page, ni liste, ni `sitemap.xml`, ni flux.
     */
    visible: z.boolean(),
    prix: PrixSchema.optional(),
    achat: AchatSchema.optional(),
    /** Faux : « Prix sur demande », et le montant ne quitte jamais la régie. */
    prixVisible: z.boolean(),
    description: TexteBilingueSchema.optional(),
    medias: z.array(MediaSchema),
})
    .refine((o) => !o.prixVisible || o.prix !== undefined, 'un prix affiché suppose un prix renseigné');
/** Une œuvre est « disponible » au sens du site si elle peut être proposée. */
export function estDisponible(oeuvre) {
    return oeuvre.statut === 'disponible' || oeuvre.statut === 'reservee';
}
/** Cartel dimensionnel : « 162 × 130 cm ». */
export function dimensionsEnTexte(dimensions, texteLibre) {
    if (dimensions === undefined)
        return '';
    if ('texte' in dimensions)
        return texteLibre;
    const nombres = [dimensions.hauteur, dimensions.largeur, dimensions.profondeur]
        .filter((n) => n !== undefined)
        .map((n) => nombreFrancais(n));
    return `${nombres.join(' × ')} ${dimensions.unite}`;
}
function nombreFrancais(n) {
    return Number.isInteger(n) ? String(n) : String(n).replace('.', ',');
}
//# sourceMappingURL=oeuvres.js.map